import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CallSwiperLogo } from "@/components/CallSwiperLogo";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "checking", email: "", error: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "invalid", email: "", error: "Missing token in the reset link." });
      return;
    }
    api.post("/auth/verify-reset-token", { token })
      .then(({ data }) => setState({ status: "valid", email: data.email, error: "" }))
      .catch((err) => setState({ status: "invalid", email: "", error: err?.response?.data?.detail || "Invalid or expired reset link." }));
  }, [token]);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col justify-between px-5 pt-10 pb-8 sm:p-8 lg:p-14 safe-top-margin safe-bottom">
        <CallSwiperLogo size={26} />
        <div className="max-w-md w-full mx-auto lg:mx-0 animate-fade-in-up">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="reset-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>

          {state.status === "checking" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="reset-checking">
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying reset link…
            </div>
          )}

          {state.status === "invalid" && (
            <div data-testid="reset-invalid-panel" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Reset link is not valid</p>
                <p className="text-muted-foreground mt-1">{state.error}</p>
                <Link to="/forgot-password" className="inline-block mt-3 text-primary hover:underline text-xs" data-testid="reset-request-new-link">
                  Request a new reset link →
                </Link>
              </div>
            </div>
          )}

          {state.status === "valid" && (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Set a new password</p>
              <h1 className="text-3xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Almost there,<br />
                <span className="text-primary">let's lock it in.</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Setting a new password for <span className="font-mono text-foreground">{state.email}</span>. Once saved, this reset link stops working.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
                  <Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm new password</Label>
                  <Input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2-input" className="h-11" />
                </div>
                <Button type="submit" className="w-full h-11 group" disabled={busy} data-testid="reset-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-2" /> Update password <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Call Swiper</p>
      </div>

      <div className="hidden lg:flex relative overflow-hidden" style={{ backgroundColor: "#0B0F17" }}>
        <div className="relative z-10 px-14 py-16 flex flex-col justify-center w-full max-w-2xl text-white">
          <h2 className="text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">
            One-time link.<br />One strong password.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/60 max-w-lg">
            Your new password is hashed with bcrypt and never stored in plain text. The reset link will be invalidated the moment you save.
          </p>
        </div>
      </div>
    </div>
  );
}
